import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import midasInstance from '@rsos/midas/src/midasInstance';
import {
  selectExistingRequest,
  selectTentativeETALocation,
} from '@rsos/reduxDataStore/src/features/airAmbulance';
import type { PSAPInfo } from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import {
  useSelectedCallerID,
  useSelectedCall,
} from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import useDebounce from '@rsos/utils/useDebounce';
import type { RootState } from '../../../../app';
import type { Coordinates, TentativeETA, ExistingRequest } from '../../types';

const TENTATIVE_UNAVAILABLE = 'Tentative ETA unavailable';
const TENTATIVE_FETCHING = 'Fetching Tentative ETA';

interface ETAResponseData {
  extSystemTripId?: string;
  tripLeg?: {
    eta?: {
      display?: string;
      providers?: string;
    };
  }[];
}

interface FromUnite {
  logged_in_fname: string;
  logged_in_lname: string;
  logged_in_email: string;
  ecc_id: string;
  caller_phone?: string;
}

/**
 * Custom hook that fetches and manages tentative ETA (Estimated Time of Arrival) data for air ambulance services.
 *
 * @returns {Object|null} Returns an object with the following structure when data is available:
 * @returns {string} returns.display - The display text for the ETA
 * @returns {string|null} returns.providers - String of providers if available, null otherwise
 *
 * Possible return values:
 * - When data is available: { display: "ETA time", providers: "provider string" }
 * - When loading: { display: "Fetching Tentative ETA", providers: null }
 * - When error: { display: "Tentative ETA unavailable", providers: null }
 * - When no coordinates: null
 */
export const useTentativeETA = (): TentativeETA | null => {
  const [etaData, setEtaData] = useState<ETAResponseData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const currentPSAP = useSelector(
    (state: RootState) => state.psaps.currentPSAP as PSAPInfo | null,
  );
  const selectedCallerID = useSelectedCallerID();
  const selectedCall = useSelectedCall();
  const existingRequest = useSelector(
    selectExistingRequest,
  ) as ExistingRequest | null;

  const isEditMode = !!existingRequest?.body;
  const tentativeCoordinates = useSelector(selectTentativeETALocation);

  const hasLocation = selectedCall ? getMostRecentLoc(selectedCall) : null;

  const fetchETA = useCallback(
    async (coords: Coordinates | null): Promise<void> => {
      if (!coords) return;

      setLoading(true);
      setError(null);

      try {
        const callflow = '/air_ambulance/gmr_air_eta';
        const fromUnite: FromUnite = {
          logged_in_fname: currentPSAP?.contact_name || '',
          logged_in_lname: currentPSAP?.contact_name || '',
          logged_in_email: currentPSAP?.contact_email || '',
          ecc_id: currentPSAP?.account_id || '',
        };

        if (!isEditMode) {
          if (hasLocation) {
            fromUnite['caller_phone'] = `+${selectedCallerID}`;
          }
        }

        const payload: {
          callflow: string;
          blocking: boolean;
          variables: {
            from_unite: FromUnite;
            ext_trip_id: string | undefined;
            trip_data: {
              location: {
                latitude: string;
                longitude: string;
              };
            };
          };
        } = {
          callflow,
          blocking: true,
          variables: {
            from_unite: fromUnite,
            ext_trip_id: '',
            trip_data: {
              location: {
                latitude: coords.latitude?.toString() || '',
                longitude: coords.longitude?.toString() || '',
              },
            },
          },
        };
        if (existingRequest && existingRequest.body) {
          payload.variables.ext_trip_id =
            existingRequest?.body?.ext_trip_id || '';
        }

        const response = await midasInstance.post(
          '/internal_auth/trigger',
          payload,
          { headers: { retry: true } },
        );

        const parsed: ETAResponseData = JSON.parse(response.data.response_body);
        if (existingRequest && existingRequest.body) {
          parsed.extSystemTripId = existingRequest?.body?.ext_trip_id;
        }

        setEtaData(parsed);
      } catch (err) {
        console.error('Error fetching tentative ETA:', err); // eslint-disable-line
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    },
    [
      currentPSAP?.contact_name,
      currentPSAP?.contact_email,
      currentPSAP?.account_id,
      isEditMode,
      existingRequest,
      hasLocation,
      selectedCallerID,
    ],
  );

  const debouncedFetchETA = useDebounce(fetchETA, 500, []);

  useEffect(() => {
    if (!loading) {
      setLoading(true);
    }
    debouncedFetchETA(tentativeCoordinates);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loading intentionally excluded to avoid infinite loop
  }, [tentativeCoordinates, debouncedFetchETA]);

  const tentativeETA = useMemo((): TentativeETA | null => {
    if (
      tentativeCoordinates &&
      !loading &&
      !error &&
      etaData &&
      etaData.extSystemTripId &&
      etaData?.tripLeg?.[0]?.eta?.display
    ) {
      return {
        display: etaData?.tripLeg?.[0]?.eta?.display,
        providers: etaData?.tripLeg?.[0]?.eta?.providers || null,
        extSystemTripId: etaData.extSystemTripId,
      };
    }
    if (!loading && error && tentativeCoordinates) {
      return {
        display: TENTATIVE_UNAVAILABLE,
        providers: null,
      };
    }
    if (tentativeCoordinates) {
      return {
        display: TENTATIVE_FETCHING,
        providers: null,
      };
    }
    return null;
  }, [etaData, error, loading, tentativeCoordinates]);

  return tentativeETA;
};

export default useTentativeETA;
